import { CarriersApi } from "@freightsimple/generated-carriers-openapi-client/apis/CarriersApi";
import { Configuration } from "@freightsimple/generated-carriers-openapi-client/runtime";
import { apiServerUrl } from "@freightsimple/app-shell/environment";
import {
  getStoredSession,
  type CarrierSession,
  type CarrierUserIdentity,
} from "@/App/Auth/carrierSession";

// The generated carrier client, configured to talk to this environment's API
// and to attach the stored session token as a bearer on every request. The
// exchange endpoint has no session yet, so the header is simply omitted then —
// the backend filter lets /auth/exchange through unauthenticated.
export function carriersApi(): CarriersApi {
  return new CarriersApi(
    new Configuration({
      basePath: apiServerUrl(),
      middleware: [
        {
          pre: async (context) => {
            const session = getStoredSession();
            if (!session) return;
            const headers = new Headers(context.init.headers);
            headers.set("Authorization", `Bearer ${session.sessionToken}`);
            return { url: context.url, init: { ...context.init, headers } };
          },
        },
      ],
    }),
  );
}

/**
 * Exchange a signed link token for a carrier session. The person's identity is
 * asserted at exchange and baked into the session server-side. Throws on
 * rejection.
 */
export async function exchangeLinkToken(
  token: string,
  identity: CarrierUserIdentity,
): Promise<CarrierSession> {
  const session = await carriersApi().exchangeCarrierLink({
    linkExchangeRequest: { token, name: identity.name, email: identity.email },
  });
  return {
    sessionToken: session.sessionToken,
    carrierIdentifier: session.carrierIdentifier,
    carrierName: session.carrierName,
    userName: session.userName,
    userEmail: session.userEmail,
  };
}

/** Resolve the signed-in carrier from the stored session token. Throws if invalid. */
export async function fetchCarrierIdentity(
  sessionToken: string,
): Promise<CarrierSession> {
  const identity = await carriersApi().getCarrierIdentity();
  return {
    sessionToken,
    carrierIdentifier: identity.carrierIdentifier,
    carrierName: identity.carrierName,
    userName: identity.userName,
    userEmail: identity.userEmail,
  };
}
